import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, DestroyRef, inject, input, OnInit, ViewChild } from '@angular/core';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { AuthenticatorService } from '@aws-amplify/ui-angular';

import {
  BreadcrumbComponent,
  BreadcrumbItemComponent,
  BreadcrumbModule,
  BreadcrumbRouterComponent,
  BreadcrumbRouterService,
  ColorModeService,
  ContainerComponent,
  DropdownComponent,
  DropdownHeaderDirective,
  DropdownItemDirective,
  DropdownMenuDirective,
  DropdownToggleDirective,
  HeaderComponent,
  HeaderNavComponent,
  HeaderTogglerDirective,
  NavItemComponent,
  NavLinkDirective,
  SidebarToggleDirective,
  ThemeDirective,
  ToasterComponent,
  ToasterPlacement,
  BadgeComponent
} from '@coreui/angular-pro';

import { IconDirective } from '@coreui/icons-angular';
import { Store } from '@ngrx/store';
import { AuthService } from 'src/app/authentication/authentication.service';
import { AppState } from 'src/app/store/app.state';
import { MessageType } from 'src/app/store/payment/models/payroll.model';
import { ToastAlertComponent } from 'src/app/views/components/ow-toast-alert/ow-toast-alert.component';
import { NotificationMessage } from 'src/app/store/ui/models/model';
import * as FeatureFlagActions from 'src/app/store/ui/actions/feature-flags.actions'
import * as UiActions from 'src/app/store/ui/actions/ui.actions'
import { environment } from 'src/environments/environment';
import { Actions, ofType } from '@ngrx/effects';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { selectNoteFilters, selectNotes } from '../../../store/main/selectors/note.selectors';
import { NoteFilters, Note } from '../../../store/main/models/note.model';
import * as NoteActions from '../../../store/main/actions/note.actions';

@Component({
  selector: 'app-secure-header',
  templateUrl: './secure-header.component.html',
  standalone: true,
  imports: [
    ContainerComponent,
    HeaderTogglerDirective,
    SidebarToggleDirective,
    IconDirective,
    HeaderNavComponent,
    NavItemComponent, NavLinkDirective,
    RouterLink, RouterLinkActive,
    NgTemplateOutlet,
    BreadcrumbModule,
    ThemeDirective,
    DropdownComponent, DropdownToggleDirective, DropdownMenuDirective, DropdownItemDirective, DropdownHeaderDirective,
    ToasterComponent,
    BadgeComponent
  ]
})
export class SecureHeaderComponent extends HeaderComponent implements OnInit {

  readonly #colorModeService = inject(ColorModeService);
  readonly colorMode = this.#colorModeService.colorMode;
  envName: string = environment.envName

  readonly colorModes = [
    { name: 'light', text: 'Light', icon: 'cilSun' },
    { name: 'dark', text: 'Dark', icon: 'cilMoon' },
    { name: 'auto', text: 'Auto', icon: 'cilContrast' }
  ];

  readonly icons = computed(() => {
    const currentMode = this.colorMode();
    return this.colorModes.find(mode => mode.name === currentMode)?.icon ?? 'cilSun';
  });

  sidebarId = input('sidebar1');

  @ViewChild(ToasterComponent) toaster!: ToasterComponent;

  userRoles!: string[];
  currentFilters?: NoteFilters;
  activeNotesCount: number = 0;

  destroyRef = inject(DestroyRef);


  constructor(private router: Router, private authService: AuthService, private amplifyService: AuthenticatorService, private store: Store<AppState>, private actions$: Actions) {
    super();
  }

  async ngOnInit() {
    const currentUser = await this.authService.getCurrentUser();
    this.userRoles = currentUser?.roles ?? []

    // Subscribe to notifications for whole application
    const notification$ = this.store.select(state => state.ui.notification);
    notification$.subscribe(
      {
        next: (notificationMessage) => {
          if (notificationMessage) {
            this.addToast(notificationMessage)
          }
        },
        error: err => {
          console.log(err);
          const notification = new NotificationMessage()
          notification.message = 'Something went wrong! Please try again!'
          notification.messageType = MessageType.Error;
          this.addToast(notification);
        }
      }
    )

    // Start loading feature flags
    const organizationId = await this.authService.getOrganizationId();
    const clientIds = await this.authService.getAllowedClients()
    this.store.dispatch(FeatureFlagActions.loadFeatureFlags({ organizationId, clientIds }))

    //Subscribe to note filters
    this.store.select(selectNoteFilters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(filters => {
        this.currentFilters = filters;
        
        // Load notes when filters are available to show counter on page load
        if (filters?.group) {
          this.store.dispatch(NoteActions.getAllNotes({ organizationId, filters }));
        }
      });

    // Subscribe to notes count for the badge
    this.store.select(selectNotes)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(notes => {
        // Count only active notes (endDate === null)
        this.activeNotesCount = notes.filter((note: Note) => note.endDate === null).length;
      });

    // this.subscribeForServiceErrors()
  }

  addToast(notification: NotificationMessage) {
    const options = {
      // title: `CoreUI for Angular Toast`,
      message: notification.message,
      delay: 8000,
      placement: ToasterPlacement.TopEnd,
      color: notification.messageType,
      autohide: true,
    }
    const componentRef = this.toaster.addToast(ToastAlertComponent, { ...options });
  }

  // Commentinng it out for now as it seems I still nneed to add the same in the page to make loadig false etc
  // subscribeForServiceErrors() {
  //   this.actions$.pipe(
  //     takeUntilDestroyed(this.destroyRef),
  //     ofType(UiActions.serviceCallFailure),
  //     // map(action => action.error)
  //   ).subscribe(({ action, error, type }) => {
  //     if (error?.status > 399 && error.status < 500) {
  //       this.store.dispatch(UiActions.addNotificationMessage({ message: error.error?.detailedErrorMessage, messageType: MessageType.Warning }))
  //     } else {
  //       this.store.dispatch(UiActions.addNotificationMessage({ message: "Unexpected error creating a payroll!", messageType: MessageType.Error }))
  //     }
  //   });
  // }



  logOut() {
    this.amplifyService.signOut()
    this.router.navigate(['/']);
    this.store.dispatch(UiActions.clearState())
  }



}
